import { useState, useEffect } from "react";
import { Phone } from "lucide-react";
import { GenieAvatar } from "@/components/GenieAvatar";
import { useResponsiveLayout } from "@/hooks/ui/useResponsiveLayout";
import { TALK_TO_GENIE_PHONE_DISPLAY } from "@/data/marketing/homepage";

export default function HomeGenieCTA() {
  const [gradientAngle, setGradientAngle] = useState(90);
  const [direction, setDirection] = useState(1);
  const { windowWidth } = useResponsiveLayout();
  const safeWidth = Math.max(windowWidth, 1);
  const viewportCap = Math.max(Math.min(safeWidth - 48, 320), 96);
  const candidateSize = Math.min(Math.max(safeWidth * 0.7, 200), 320);
  const avatarSize = Math.round(Math.min(Math.min(candidateSize, viewportCap), safeWidth));
  
  useEffect(() => {
    const interval = setInterval(() => {
      setGradientAngle((prev) => {
        const newAngle = prev + (direction * 3);
        if (newAngle >= 270 || newAngle <= 90) {
          setDirection(d => -d);
        }
        return newAngle;
      });
    }, 30);
    return () => clearInterval(interval);
  }, [direction]);

  return (
    <section className="w-full py-20 sm:py-24 md:py-32 relative overflow-hidden bg-white">
      {/* Subtle decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-genie-primary/20 to-transparent" />
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-center md:justify-between gap-8">
        {/* Left Column: Text Content */}
        <div className="md:w-1/2 text-center md:text-left mb-8 sm:mb-12 md:mb-0">
          <div className="max-w-lg mx-auto md:mx-0">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#222B45] mb-4">
              Talk to a live <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">Help Genie</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8">
              Don't take our word for it. Have a conversation. The same genie that answers your customers will show you exactly how it sounds, books work, and captures leads.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-3 sm:gap-4">
              <a
                href="/live-agent/business-onboarding"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-base sm:text-lg"
                style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
              >
                <Phone className="h-5 w-5" />
                Talk to a live genie
              </a>
              <a
                href="/talk-to-genie"
                className="inline-flex items-center px-6 sm:px-8 py-3 rounded-xl border-2 border-[#E5EAF0] text-gray-700 font-semibold transition-all hover:border-genie-primary/40 hover:text-genie-primary text-base sm:text-lg"
              >
                More ways to try it
              </a>
            </div>
            <p className="mt-4 text-sm text-gray-500">{TALK_TO_GENIE_PHONE_DISPLAY}</p>
          </div>
        </div>

        {/* Right Column: Animated Genie Image */}
        <div className="md:w-1/2 w-full flex justify-center items-center px-0">
          <div className="mx-auto" style={{ width: avatarSize }}>
            <a
              href="/talk-to-genie"
              className="transform block w-full sm:scale-95 lg:scale-100"
            >
              <GenieAvatar
                speaking={true}
                color="#4E9CFF"
                secondaryColor="#FF4DB8"
                gradientEnabled={true}
                gradientAngle={gradientAngle}
                customAvatarUrl="/genie-mascots/role/concierge.png"
                size={avatarSize}
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
